import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    Alert, Box, Badge, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Typography, TextField, Select, MenuItem, FormControl, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, IconButton, Menu, MenuItem as MenuItemDropdown, InputAdornment, CircularProgress, Tooltip, Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PreviewDialogBox from "../../Model/PreviewDialogBox";
import { getJobs, DeleteJobs, getJobById, updateJobs, updateJobStatusApi, getActiveLocations, downloadJobs } from "../../Api/JobsServiceApi";

const Jobs = () => {
    const [locationFilter, setLocationFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentJobId, setCurrentJobId] = useState(null);
    const [sortOrder, setSortOrder] = useState({ field: "", direction: "" });

    // Filter Modal State
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState({
        location: "",
        status: "",
        search: "",
    });

    const [appliedFilters, setAppliedFilters] = useState({
        location: "",
        status: "",
        search: "",
    });

    const activeFiltersCount = Object.values(appliedFilters).filter(Boolean).length;

    const handleFilterModalOpen = () => {
        setTempFilters(appliedFilters);
        setFilterModalOpen(true);
    };

    const handleFilterModalClose = () => {
        setFilterModalOpen(false);
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            location: "",
            status: "",
            search: "",
        };
        setTempFilters(clearedFilters);
        setAppliedFilters(clearedFilters);
        setFilterModalOpen(false);
        setLocationFilter("");
        setStatusFilter("");
        setSearchQuery("");
        fetchJobs();
    };

    const handleApplyFilters = () => {
        setAppliedFilters(tempFilters);
        setFilterModalOpen(false);
        setLocationFilter(tempFilters.location);
        setStatusFilter(tempFilters.status);
        setSearchQuery(tempFilters.search);
        fetchJobs({
            location: tempFilters.location || undefined,
            status: tempFilters.status || undefined,
            search: tempFilters.search.trim() || undefined,
        });
    };

    const handleSearch = () => {
        setAppliedFilters(prev => ({
            ...prev,
            location: locationFilter,
            status: statusFilter,
            search: searchQuery
        }));
        fetchJobs({
            location: locationFilter || undefined,
            status: statusFilter || undefined,
            search: searchQuery.trim() || undefined,
        });
    };


    // Preview Dialog State
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedJobData, setSelectedJobData] = useState({});
    const [originalJobData, setOriginalJobData] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [benefits, setBenefits] = useState([
        "PF", "Insurance", "Performance bonus", "5 days week",
        "Health Insurance", "Paid Time Off", "Free meals", "Other"
    ]);
    const [selectedBenefits, setSelectedBenefits] = useState([]);
    const [skills, setSkills] = useState([]);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const isMountedRef = useRef(true);

    const normalizeJobData = (job) => {
        if (!job) return {};
        
        // Resolve locations
        const primaryLocation = Array.isArray(job.JobLocations) && job.JobLocations.length > 0
            ? job.JobLocations[0]
            : null;
        const locationParts = (job.location || '').split(',').map(s => s.trim());
        const city = primaryLocation?.city || job.city || locationParts[0] || '';
        const area = primaryLocation?.area || job.area || locationParts[1] || '';
        const pinCode = primaryLocation?.pincode || primaryLocation?.pinCode || job.pinCode || '';

        // Normalize benefits to objects
        const benefitsArray = job.JobBenefits || job.job_benefits || job.jobBenefits || [];
        const normalizedBenefits = Array.isArray(benefitsArray) && benefitsArray.length > 0
            ? benefitsArray.map(b => (typeof b === 'string' ? { id: b, name: b } : { id: b.benefitId || b.id, name: b.name || '' })).filter(b => b.name)
            : Array.isArray(job.benefits)
            ? job.benefits.map(b => (typeof b === 'string' ? { id: b, name: b } : { id: b.benefitId || b.id || b.benefitId, name: b.name || '' })).filter(b => b.name)
            : [];

        // Normalize skills to objects
        const skillsArray = job.JobSkills || job.job_skills || job.jobSkills || [];
        const normalizedSkills = Array.isArray(skillsArray) && skillsArray.length > 0
            ? skillsArray.map(s => {
                const skillObj = s.skill || s;
                return {
                    id: skillObj.id || s.skillId || s.id,
                    name: skillObj.name || skillObj.skillName || ''
                };
            }).filter(s => s.name)
            : Array.isArray(job.skills)
            ? job.skills.map(s => {
                const skillObj = s.skill || s;
                return {
                    id: skillObj.id || s.skillId || s.id,
                    name: skillObj.name || skillObj.skillName || ''
                };
            }).filter(s => s.name)
            : [];

        return {
            ...job,
            city,
            area,
            pinCode,
            benefits: normalizedBenefits,
            skills: normalizedSkills,
        };
    };

    const [jobsData, setJobsData] = useState([]);
    const [loadingJobs, setLoadingJobs] = useState(true);
    const [jobsError, setJobsError] = useState("");
    const [deleteStatus, setDeleteStatus] = useState({
        loading: false,
        error: "",
        success: "",
    });
    const [jobDetailsLoading, setJobDetailsLoading] = useState(false);
    const [jobDetailsError, setJobDetailsError] = useState("");
    const [statusUpdatingJobIds, setStatusUpdatingJobIds] = useState([]);
    const [updateStatus, setUpdateStatus] = useState({
        loading: false,
        error: "",
        success: "",
    });
    const [availableLocations, setAvailableLocations] = useState([]);

    useEffect(() => {
        isMountedRef.current = true;
        
        const fetchLocations = async () => {
            try {
                const res = await getActiveLocations();
                if (res?.success && isMountedRef.current) {
                    setAvailableLocations(res.locations || []);
                }
            } catch (err) {
                console.error("Failed to load active locations:", err);
            }
        };
        fetchLocations();

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchJobs = useCallback(async (filters = {}) => {
        setLoadingJobs(true);
        setJobsError("");
        try {
            const data = await getJobs(filters);
            if (!isMountedRef.current) {
                return;
            }
            const normalizedData = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
            setJobsData(normalizedData);
        } catch (error) {
            if (!isMountedRef.current) {
                return;
            }
            const message =
                error?.response?.data?.message || error?.message || "Failed to load jobs.";
            setJobsError(message);
        } finally {
            if (isMountedRef.current) {
                setLoadingJobs(false);
            }
        }
    }, [getJobs]);

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    useEffect(() => {
        setSelectedJobs([]);
    }, [jobsData]);

    const handleMenuOpen = (event, jobId) => {
        setAnchorEl(event.currentTarget);
        setCurrentJobId(jobId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setCurrentJobId(null);
    };

    // Preview Dialog Handlers
    const handleRowClick = (event, job) => {
        // Ignore clicks on checkbox, select dropdown, and more menu
        if (
            event.target.closest('input[type="checkbox"]') ||
            event.target.closest('.MuiSelect-select') ||
            event.target.closest('.MuiIconButton-root')
        ) {
            return;
        }

        const normalizedJob = normalizeJobData(job);
        setSelectedJobData(normalizedJob);
        setOriginalJobData(null);
        setSelectedBenefits(normalizedJob.benefits);
        setSelectedSkills(normalizedJob.skills);
        setIsEditMode(false);
        setPreviewOpen(true);
    };

    const handleView = () => {
        const job = jobsData.find(j => j.id === currentJobId);
        if (job) {
            const normalizedJob = normalizeJobData(job);
            setSelectedJobData(normalizedJob);
            setOriginalJobData(null);
            setSelectedBenefits(normalizedJob.benefits);
            setSelectedSkills(normalizedJob.skills);
            setIsEditMode(false);
            setPreviewOpen(true);
        }
        handleMenuClose();
    };

    const handleEdit = async () => {
        if (!currentJobId || jobDetailsLoading) return;

        setJobDetailsLoading(true);
        setJobDetailsError("");
        try {
            const response = await getJobById(currentJobId);
            const job = response?.data ?? response;
            if (job) {
                const normalizedJob = normalizeJobData(job);
                setSelectedJobData(normalizedJob);
                setOriginalJobData(JSON.parse(JSON.stringify(normalizedJob)));
                setSelectedBenefits(normalizedJob.benefits);
                setSelectedSkills(normalizedJob.skills);
                setIsEditMode(true);
                setPreviewOpen(true);
            } else {
                setJobDetailsError("Job data not found.");
            }
        } catch (error) {
            const message =
                error?.response?.data?.message || error?.message || "Failed to load job details.";
            setJobDetailsError(message);
        } finally {
            setJobDetailsLoading(false);
            handleMenuClose();
        }
    };

    const resetDeleteStatus = () => {
        setDeleteStatus((prev) => ({ ...prev, error: "", success: "" }));
    };

    const deleteJobById = async (jobId) => {
        if (!jobId) return;

        setDeleteStatus((prev) => ({ ...prev, loading: true, error: "", success: "" }));
        try {
            await DeleteJobs(jobId);
            setJobsData((prev) => prev.filter((job) => job.id !== jobId));
            setSelectedJobs((prev) => prev.filter((id) => id !== jobId));
            setDeleteStatus({ loading: false, error: "", success: "Job deleted successfully." });
        } catch (error) {
            const message =
                typeof error === "string"
                    ? error
                    : error?.response?.data?.message || error?.message || "Failed to delete job.";
            setDeleteStatus({ loading: false, error: message, success: "" });
        }
    };

    const handleDelete = async () => {
        if (!currentJobId || deleteStatus.loading) return;
        await deleteJobById(currentJobId);
        handleMenuClose();
    };

    const handleBulkDelete = async () => {
        if (selectedJobs.length === 0 || deleteStatus.loading) return;

        setDeleteStatus({ loading: true, error: "", success: "" });
        try {
            await Promise.all(selectedJobs.map((jobId) => DeleteJobs(jobId)));
            setJobsData((prev) => prev.filter((job) => !selectedJobs.includes(job.id)));
            setSelectedJobs([]);
            setDeleteStatus({ loading: false, error: "", success: "Selected jobs deleted successfully." });
        } catch (error) {
            const message =
                typeof error === "string" ? error : error?.response?.data?.message || error?.message || "Failed to delete selected jobs.";
            setDeleteStatus({ loading: false, error: message, success: "" });
        }
    };

    useEffect(() => {
        if (updateStatus.success || updateStatus.error) {
            const timer = setTimeout(() => {
                setUpdateStatus((prev) => ({ ...prev, success: "", error: "" }));
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [updateStatus.success, updateStatus.error]);

    const handlePreviewClose = () => {
        setPreviewOpen(false);
        setSelectedJobData({});
        setOriginalJobData(null);
        setIsEditMode(false);
        setSelectedBenefits([]);
        setSelectedSkills([]);
        setUpdateStatus({ loading: false, error: "", success: "" });
    };

    const handleBenefitToggle = (benefit) => {
        setSelectedBenefits((prev) => {
            const exists = prev.some((b) => b.id === benefit.id);
            const updated = exists ? prev.filter((b) => b.id !== benefit.id) : [...prev, benefit];
            setSelectedJobData((job) => ({ ...job, benefits: updated }));
            return updated;
        });
    };

    const handleBenefitsChange = (newBenefits) => {
        setSelectedBenefits(newBenefits);
        setSelectedJobData((prev) => ({ ...prev, benefits: newBenefits }));
    };

    const handleSkillToggle = (skill) => {
        setSelectedSkills((prev) => {
            const exists = prev.some((s) => s.id === skill.id);
            const updated = exists ? prev.filter((s) => s.id !== skill.id) : [...prev, skill];
            setSelectedJobData((job) => ({ ...job, skills: updated }));
            return updated;
        });
    };

    const handleSkillsChange = (newSkills) => {
        setSelectedSkills(newSkills);
        setSelectedJobData((prev) => ({ ...prev, skills: newSkills }));
    };

    const handleFieldChange = (field, value) => {
        setSelectedJobData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const editableFields = [
        "title",
        "city",
        "area",
        "pinCode",
        "recruitmentTimeline",
        "numberOfPeople",
        "jobType",
        "payMaximum",
        "payPeriod",
        "description",
        "benefits",
    ];
    const arraysAreEqual = (arrA = [], arrB = []) => {
        if (arrA.length !== arrB.length) return false;
        return arrA.every((value, index) => value === arrB[index]);
    };

    function checkHasChanges() {
        if (!originalJobData) return false;
        
        // Compare standard fields
        const standardFields = [
            "title", "city", "area", "pinCode", "recruitmentTimeline",
            "numberOfPeople", "jobType", "payMaximum", "payPeriod", "description"
        ];
        const standardChanged = standardFields.some(field => {
            const orig = originalJobData[field] ?? "";
            const curr = selectedJobData[field] ?? "";
            return String(orig).trim() !== String(curr).trim();
        });
        if (standardChanged) return true;

        // Compare benefits
        const origBenefits = Array.isArray(originalJobData.benefits) ? originalJobData.benefits : [];
        const origBenefitIds = origBenefits.map(b => b.id || b.benefitId).filter(Boolean);
        const currBenefitIds = selectedBenefits.map(b => b.id || b.benefitId).filter(Boolean);
        const benefitsChanged = origBenefitIds.length !== currBenefitIds.length ||
            !currBenefitIds.every(id => origBenefitIds.includes(id));
        if (benefitsChanged) return true;

        // Compare skills
        const origSkills = Array.isArray(originalJobData.skills) ? originalJobData.skills : [];
        const origSkillIds = origSkills.map(s => s.id || s.skillId).filter(Boolean);
        const currSkillIds = selectedSkills.map(s => s.id || s.skillId).filter(Boolean);
        const skillsChanged = origSkillIds.length !== currSkillIds.length ||
            !currSkillIds.every(id => origSkillIds.includes(id));
        if (skillsChanged) return true;

        return false;
    }

    const handleSubmit = async () => {
        if (!isEditMode) {
            handlePreviewClose();
            return;
        }

        if (!selectedJobData?.id) {
            setUpdateStatus({ loading: false, error: "Job ID missing. Unable to update.", success: "" });
            return;
        }

        if (!originalJobData) {
            setUpdateStatus({ loading: false, error: "Original job data missing. Please re-open the editor.", success: "" });
            return;
        }

        const payload = {};

        // Construct standard changes
        const standardFields = [
            "title", "city", "area", "pinCode", "recruitmentTimeline",
            "numberOfPeople", "jobType", "payMaximum", "payPeriod", "description"
        ];
        standardFields.forEach(field => {
            const originalValue = originalJobData[field] ?? "";
            const newValue = selectedJobData[field] ?? "";
            if (String(newValue).trim() !== String(originalValue).trim()) {
                payload[field] = newValue;
            }
        });

        // Compare and format benefits
        const originalBenefits = Array.isArray(originalJobData.benefits) ? originalJobData.benefits : [];
        const originalBenefitIds = originalBenefits.map(b => b.id || b.benefitId).filter(Boolean);
        const newBenefitIds = selectedBenefits.map(b => b.id || b.benefitId).filter(Boolean);
        
        const benefitsChanged = originalBenefitIds.length !== newBenefitIds.length ||
            !newBenefitIds.every(id => originalBenefitIds.includes(id));
            
        if (benefitsChanged) {
            payload.benefits = selectedBenefits.map(b => ({ benefitId: b.id || b.benefitId, name: b.name }));
        }

        // Compare and format skills
        const originalSkills = Array.isArray(originalJobData.skills) ? originalJobData.skills : [];
        const originalSkillIds = originalSkills.map(s => s.id || s.skillId).filter(Boolean);
        const newSkillIds = selectedSkills.map(s => s.id || s.skillId).filter(Boolean);

        const skillsChanged = originalSkillIds.length !== newSkillIds.length ||
            !newSkillIds.every(id => originalSkillIds.includes(id));

        if (skillsChanged) {
            payload.skills = selectedSkills.map(s => ({ skillId: s.id || s.skillId, name: s.name || s.skillName, importance: 1 }));
        }

        if (Object.keys(payload).length === 0) {
            setUpdateStatus({ loading: false, error: "No changes detected to update.", success: "" });
            return;
        }

        payload.jobId = selectedJobData.id;
        setUpdateStatus({ loading: true, error: "", success: "" });
        try {
            await updateJobs(selectedJobData.id, payload);
            setJobsData((prev) =>
                prev.map((job) => job.id === selectedJobData.id ? { ...job, ...payload } : job)
            );
            setUpdateStatus({ loading: false, error: "", success: "Job updated successfully." });
            handlePreviewClose();
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || "Failed to update job.";
            setUpdateStatus({ loading: false, error: message, success: "" });
        }
    };


    const resetUpdateStatus = () => {
        setUpdateStatus({ loading: false, error: "", success: "" });
    };

    const handleDownloadSelectedJobs = async () => {
        if (!selectedJobs.length) {
            return;
        }

        try {
            const blob = await downloadJobs(selectedJobs);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `selected-jobs-${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download selected jobs:", error);
            setJobsError("Unable to export jobs. Please try again.");
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedJobs(filteredJobs.map((job) => job.id));
        } else {
            setSelectedJobs([]);
        }
    };

    const handleSelectJob = (jobId) => {
        setSelectedJobs((prev) =>
            prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
        );
    };

    const handleSort = (field) => {
        const direction =
            sortOrder.field === field && sortOrder.direction === "asc" ? "desc" : "asc";
        setSortOrder({ field, direction });

        const sortedData = [...jobsData].sort((a, b) => {
            if (field === "title") {
                return direction === "asc" ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
            } else if (field === "date") {
                return direction === "asc" ? new Date(a.datePosted) - new Date(b.datePosted) : new Date(b.datePosted) - new Date(a.datePosted);
            }
            return 0;
        });
        setJobsData(sortedData);
    };

    const handleStatusChange = async (jobId, newStatus) => {
        setStatusUpdatingJobIds((prev) => [...prev, jobId]);
        try {
            await updateJobStatusApi(jobId, newStatus);
            setJobsData((prev) =>
                prev.map((job) =>
                    job.id === jobId ? { ...job, status: newStatus } : job
                )
            );
            setUpdateStatus({ success: "Status updated successfully", error: "", loading: false });
            setTimeout(() => setUpdateStatus({ success: "", error: "", loading: false }), 3000);
        } catch (error) {
            console.error("Failed to update status:", error);
            setUpdateStatus({ error: "Failed to update status", success: "", loading: false });
        } finally {
            setStatusUpdatingJobIds((prev) => prev.filter((id) => id !== jobId));
        }
    };

    const filteredJobs = jobsData;

    return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, backgroundColor: '#F8F9FA', minHeight: 'calc(100vh - 120px)', maxWidth: '1400px', mx: 'auto' }}>
            {/* Header */}
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>Jobs</Typography>

            {jobsError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobsError("")}>
                    {jobsError}
                </Alert>
            )}
            {deleteStatus.error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={resetDeleteStatus}>
                    {deleteStatus.error}
                </Alert>
            )}
            {deleteStatus.success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={resetDeleteStatus}>
                    {deleteStatus.success}
                </Alert>
            )}
            {jobDetailsError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobDetailsError("")}>
                    {jobDetailsError}
                </Alert>
            )}
            {updateStatus.error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={resetUpdateStatus}>
                    {updateStatus.error}
                </Alert>
            )}
            {updateStatus.success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={resetUpdateStatus}>
                    {updateStatus.success}
                </Alert>
            )}

            {/* Filters Section */}
            {selectedJobs.length > 0 ? (
                // Selection Bar - shown when jobs are selected
                <Box
                    sx={{
                        backgroundColor: '#FFFFFF', p: 2.5, borderTopLeftRadius: 12, borderTopRightRadius: 12, border: '1px solid #E5E7EB', borderBottom: 'none',
                        display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center",
                        justifyContent: { xs: "flex-start", md: "space-between" },
                    }}
                >
                    {/* Left Section */}
                    <Box
                        sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", flex: { xs: "1 1 100%", md: "0 1 auto" }, }}                    >
                        {/* Selected Count */}
                        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#666', flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                            Selected ({selectedJobs.length})
                        </Typography>

                        {/* Set Status Dropdown */}
                        <FormControl sx={{
                            minWidth: { xs: '100%', sm: 180 }, backgroundColor: 'white', flex: { xs: '1 1 100%', sm: '0 1 auto' }
                        }} size="small">
                            <Select displayEmpty defaultValue="" sx={{ "& .MuiSelect-select": { py: 1, }, }}  >
                                <MenuItem value="">Set Status</MenuItem>
                                <MenuItem value="Open">Open</MenuItem>
                                <MenuItem value="Paused">Paused</MenuItem>
                                <MenuItem value="Closed">Closed</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Clear All Button */}
                        <Button disableRipple disableElevation disableTouchRipple disableFocusRipple variant="outlined" size="small" startIcon={<CloseIcon />} onClick={() => setSelectedJobs([])}
                            sx={{
                                borderColor: "", color: "#000000", textTransform: "none", fontWeight: 500,
                                backgroundColor: "#ffffff", p: 1, flex: { xs: '1 1 100%', sm: '0 1 auto' },
                                "&:hover": { borderColor: "#333", backgroundColor: "rgba(0,0,0,0.04)", },
                            }}
                        >
                            Clear All
                        </Button>
                        <Button disableRipple disableElevation disableTouchRipple disableFocusRipple variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadSelectedJobs}
                            sx={{
                                borderColor: '#E5E7EB', color: '#6B31C4', textTransform: 'none',
                                fontWeight: 600, p: 1, backgroundColor: "#ffffff",
                                flex: { xs: '1 1 calc(50% - 8px)', sm: '0 1 auto' },
                                "&:hover": { borderColor: "#6a2fa0", backgroundColor: "rgba(123, 58, 183, 0.04)", },
                            }}
                        >
                            Download
                        </Button>

                        {/* Delete Button */}
                        <Button disableRipple disableElevation disableTouchRipple disableFocusRipple variant="outlined" size="small" startIcon={<DeleteOutlineIcon />}
                            onClick={handleBulkDelete}
                            disabled={deleteStatus.loading}
                            sx={{
                                borderColor: "", color: "#D32F2F", textTransform: "none", fontWeight: 600, backgroundColor: "#ffffff", p: 1,
                                flex: { xs: '1 1 calc(50% - 8px)', sm: '0 1 auto' },
                                "&:hover": { borderColor: "#b71c1c", backgroundColor: "rgba(211, 47, 47, 0.04)" },
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        backgroundColor: '#FFFFFF', p: 2.5, borderTopLeftRadius: 12, borderTopRightRadius: 12, border: '1px solid #E5E7EB', borderBottom: 'none',
                        display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center",
                    }}
                >
                    {/* Mobile Filter Button - Visible only on small devices */}
                    <Badge
                        badgeContent={activeFiltersCount}
                        color="error"
                        sx={{
                            display: { xs: "block", md: "none" },
                            "& .MuiBadge-badge": {
                                right: -3,
                                top: 3,
                            }
                        }}
                    >
                        <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={handleFilterModalOpen}
                            sx={{
                                borderColor: '#6B31C4',
                                color: '#6B31C4',
                                textTransform: "none",
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                backgroundColor: "white",
                                "&:hover": {
                                    borderColor: "#6a2fa0",
                                    backgroundColor: "rgba(123, 58, 183, 0.04)",
                                },
                            }}
                        >
                            Filters
                        </Button>
                    </Badge>

                    {/* Desktop Filters - Visible only on medium and larger devices */}

                    {/* Location Filter */}
                    <FormControl
                        sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(25% - 16px)", md: "1 1 calc(20% - 16px)", }, backgroundColor: "white", }}
                        size="small"
                    >
                        <Select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} displayEmpty >
                            <MenuItem value="">Location</MenuItem>
                            {availableLocations.map((loc) => (
                                <MenuItem key={loc.id} value={loc.name}>
                                    {loc.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Status Filter */}
                    <FormControl
                        sx={{
                            flex: { xs: "1 1 100%", sm: "1 1 calc(25% - 16px)", md: "1 1 calc(20% - 16px)", }, backgroundColor: "white",
                        }} size="small"
                    >
                        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} displayEmpty                    >
                            <MenuItem value="">Status</MenuItem>
                            <MenuItem value="Open">Open</MenuItem>
                            <MenuItem value="Paused">Paused</MenuItem>
                            <MenuItem value="Closed">Closed</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Search */}
                    <TextField
                        placeholder="Search Job title"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 16px)", md: "1 1 calc(25% - 16px)" },
                            backgroundColor: "white",
                            "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon sx={{ color: "#666" }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Search Button */}
                    <Button variant="contained" onClick={handleSearch}
                        sx={{
                            flex: { xs: '1 1 100%', sm: '0 0 auto' },
                            px: 3, py: 1
                        }}
                    >
                        Search
                    </Button>

                    {/* Applied Filters Display for Mobile */}
                    {activeFiltersCount > 0 && (
                        <Box sx={{
                            display: { xs: "flex", md: "none" },
                            gap: 1,
                            flexWrap: "wrap",
                            flex: 1,
                            width: "100%"
                        }}>
                            {appliedFilters.location && (
                                <Chip
                                    label={`Location: ${appliedFilters.location}`}
                                    onDelete={() => {
                                        setAppliedFilters(prev => {
                                            const next = { ...prev, location: "" };
                                            fetchJobs({
                                                location: undefined,
                                                status: next.status || undefined,
                                                search: next.search.trim() || undefined
                                            });
                                            return next;
                                        });
                                        setLocationFilter("");
                                    }}
                                    sx={{ backgroundColor: "white" }}
                                />
                            )}
                            {appliedFilters.status && (
                                <Chip
                                    label={`Status: ${appliedFilters.status}`}
                                    onDelete={() => {
                                        setAppliedFilters(prev => {
                                            const next = { ...prev, status: "" };
                                            fetchJobs({
                                                location: next.location || undefined,
                                                status: undefined,
                                                search: next.search.trim() || undefined
                                            });
                                            return next;
                                        });
                                        setStatusFilter("");
                                    }}
                                    sx={{ backgroundColor: "white" }}
                                />
                            )}
                            {appliedFilters.search && (
                                <Chip
                                    label={`Search: ${appliedFilters.search}`}
                                    onDelete={() => {
                                        setAppliedFilters(prev => {
                                            const next = { ...prev, search: "" };
                                            fetchJobs({
                                                location: next.location || undefined,
                                                status: next.status || undefined,
                                                search: undefined
                                            });
                                            return next;
                                        });
                                        setSearchQuery("");
                                    }}
                                    sx={{ backgroundColor: "white" }}
                                />
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {/* Filter Modal */}
            <Dialog
                open={filterModalOpen}
                onClose={handleFilterModalClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{
                    backgroundColor: '#FFFFFF',
                    color: '#1A1A2E',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderBottom: '1px solid #E5E7EB',
                }}>
                    <FilterListIcon sx={{ color: '#6B31C4' }} />
                    Filter Jobs
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

                        {/* Location Filter */}
                        <FormControl fullWidth>
                            <Typography sx={{ mb: 1, fontWeight: 500, color: "#333" }}>
                                Location
                            </Typography>
                            <Select
                                value={tempFilters.location}
                                onChange={(e) => setTempFilters(prev => ({ ...prev, location: e.target.value }))}
                                displayEmpty
                                size="small"
                            >
                                <MenuItem value="">All Locations</MenuItem>
                                {availableLocations.map((loc) => (
                                    <MenuItem key={loc.id} value={loc.name}>
                                        {loc.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Status Filter */}
                        <FormControl fullWidth>
                            <Typography sx={{ mb: 1, fontWeight: 500, color: "#333" }}>
                                Status
                            </Typography>
                            <Select
                                value={tempFilters.status}
                                onChange={(e) => setTempFilters(prev => ({ ...prev, status: e.target.value }))}
                                displayEmpty
                                size="small"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="Open">Open</MenuItem>
                                <MenuItem value="Paused">Paused</MenuItem>
                                <MenuItem value="Closed">Closed</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Search */}
                        <Box>
                            <Typography sx={{ mb: 1, fontWeight: 500, color: "#333" }}>
                                Search Job Title
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter job title to search..."
                                size="small"
                                value={tempFilters.search}
                                onChange={(e) => setTempFilters(prev => ({ ...prev, search: e.target.value }))}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: "#666" }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, gap: 1 }}>
                    <Button
                        onClick={handleClearFilters}
                        variant="outlined"
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderColor: "#D32F2F",
                            color: "#D32F2F",
                            "&:hover": {
                                borderColor: "#b71c1c",
                                backgroundColor: "rgba(211, 47, 47, 0.04)",
                            },
                        }}
                    >
                        Clear All
                    </Button>
                    <Button
                        onClick={handleFilterModalClose}
                        variant="outlined"
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApplyFilters}
                        variant="contained"
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            backgroundColor: "#7B3AB7",
                            "&:hover": {
                                backgroundColor: "#6a2fa0",
                            },
                        }}
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Table */}
            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 0,
                    borderBottomLeftRadius: 12,
                    borderBottomRightRadius: 12,
                    border: '1px solid #E5E7EB',
                    borderTop: 'none',
                    boxShadow: 'none',
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
                            <TableCell padding="checkbox">
                                <Checkbox checked={selectedJobs.length === filteredJobs.length && filteredJobs.length > 0}
                                    indeterminate={
                                        selectedJobs.length > 0 && selectedJobs.length < filteredJobs.length
                                    }
                                    onChange={handleSelectAll}
                                />
                            </TableCell>
                            <TableCell sx={{ color: '#6B7280', fontWeight: 600, cursor: 'pointer', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }} onClick={() => handleSort('title')}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    Job Title
                                    <Box sx={{ display: "flex", flexDirection: "column", ml: 0.5 }}>
                                        <ArrowUpwardIcon
                                            sx={{ fontSize: 14, mb: -0.5, opacity: sortOrder.field === "title" && sortOrder.direction === "asc" ? 1 : 0.3, }}
                                        />
                                        <ArrowDownwardIcon
                                            sx={{ fontSize: 14, opacity: sortOrder.field === "title" && sortOrder.direction === "desc" ? 1 : 0.3, }}
                                        />
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell sx={{ color: '#6B7280', fontWeight: 600, cursor: 'pointer', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }} onClick={() => handleSort('date')}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    Date Posted
                                    <Box sx={{ display: "flex", flexDirection: "column", ml: 0.5 }}    >
                                        <ArrowUpwardIcon
                                            sx={{
                                                fontSize: 14, mb: -0.5, opacity: sortOrder.field === "date" && sortOrder.direction === "asc" ? 1 : 0.3,
                                            }}
                                        />
                                        <ArrowDownwardIcon
                                            sx={{
                                                fontSize: 14, opacity: sortOrder.field === "date" && sortOrder.direction === "desc" ? 1 : 0.3,
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell sx={{ color: '#6B7280', fontWeight: 600, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Candidates
                            </TableCell>
                            <TableCell sx={{ color: '#6B7280', fontWeight: 600, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Status
                            </TableCell>
                            <TableCell sx={{ color: '#6B7280', fontWeight: 600, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                More
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingJobs ? (
                            Array.from(new Array(5)).map((_, index) => (
                                <TableRow key={index} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                                    <TableCell padding="checkbox">
                                        <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: 0.5 }} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton variant="text" width="80%" height={24} />
                                        <Skeleton variant="text" width="40%" height={16} sx={{ mt: 0.5 }} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton variant="text" width="60%" height={20} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton variant="text" width="45%" height={20} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 4 }} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton variant="circular" width={28} height={28} />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : filteredJobs.length > 0 ? (
                            filteredJobs.map((job, index) => (
                                <TableRow key={job.id}
                                    //  onClick={(e) => handleRowClick(e, job)}
                                    sx={{
                                        // backgroundColor: index % 2 === 0 ? "white" : "#F9F9F9",
                                        '&:hover': { backgroundColor: '#FAFBFC' },
                                        borderBottom: '1px solid #F3F4F6', transition: 'background-color 0.15s ease', cursor: 'pointer',
                                    }}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox checked={selectedJobs.includes(job.id)}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleSelectJob(job.id);
                                            }}
                                            sx={{ color: '#D1D5DB', '&.Mui-checked': { color: '#6B31C4' } }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 500, color: '#1A1A2E', maxWidth: 300, wordWrap: 'break-word' }}>
                                        {job.title}
                                    </TableCell>
                                    <TableCell>{job.datePosted}</TableCell>
                                    <TableCell>
                                        <Typography sx={{ color: "#065F46", fontWeight: 600 }}>
                                            {job.candidatesAll || job.candidates?.all || 0}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <FormControl size="small" sx={{ minWidth: 120 }}>
                                            {(() => {
                                                const isUpdating = statusUpdatingJobIds.includes(job.id);
                                                const statusColor = job.status?.toLowerCase() === 'active' ? '#673ab7' : job.status?.toLowerCase() === 'closed' ? '#D32F2F' : job.status?.toLowerCase() === 'paused' ? '#F57C00' : '#FFC107';
                                                
                                                if (isUpdating) {
                                                    return (
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '38px', minWidth: '120px' }}>
                                                            <CircularProgress size={20} sx={{ color: statusColor }} />
                                                        </Box>
                                                    );
                                                }

                                                return (
                                                    <Select
                                                        value={job.status?.toLowerCase() || "active"}
                                                        onChange={(e) => handleStatusChange(job.id, e.target.value)}
                                                        IconComponent={KeyboardArrowDownIcon}
                                                        sx={{
                                                            backgroundColor: job.status?.toLowerCase() === 'active' ? '#ede7f6' : job.status?.toLowerCase() === 'closed' ? '#ffebee' : job.status?.toLowerCase() === 'paused' ? '#fff3e0' : '#fff8e1',
                                                            color: statusColor,
                                                            fontWeight: 600, borderRadius: 5, border: "none",
                                                            "& .MuiOutlinedInput-notchedOutline": {
                                                                border: "none",
                                                            },
                                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                                border: "none",
                                                            },
                                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                                border: "none",
                                                            },
                                                            "& .MuiSelect-icon": {
                                                                color: statusColor,
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="active" sx={{ color: "#673ab7", fontWeight: 500 }}>
                                                            Active
                                                        </MenuItem>
                                                        <MenuItem value="closed" sx={{ color: "#D32F2F", fontWeight: 500 }}>
                                                            Closed
                                                        </MenuItem>
                                                        <MenuItem value="paused" sx={{ color: "#F57C00", fontWeight: 500 }}>
                                                            Paused
                                                        </MenuItem>
                                                        <MenuItem value="pending" sx={{ color: "#FFC107", fontWeight: 500 }}>
                                                            Pending
                                                        </MenuItem>
                                                    </Select>
                                                );
                                            })()}
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={(e) => handleMenuOpen(e, job.id)} size="small">
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <Typography sx={{ color: "#666", fontWeight: 500 }}>
                                        {jobsError ? "Unable to load jobs at the moment." : "No jobs found with the selected filters."}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* More Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right", }} transformOrigin={{ vertical: "top", horizontal: "right", }}
                PaperProps={{ sx: { boxShadow: '0 4px 16px rgba(0,0,0,0.08)', borderRadius: '10px', minWidth: 150, border: '1px solid #E5E7EB' } }}
            >
                <MenuItemDropdown onClick={handleView} sx={{ gap: 1, py: 1.5, color: "#1976D2", "&:hover": { backgroundColor: "#E3F2FD" }, }}  >
                    <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                    View
                </MenuItemDropdown>
                <MenuItemDropdown onClick={handleEdit} disabled={jobDetailsLoading}
                    sx={{
                        gap: 1, py: 1.5, color: jobDetailsLoading ? "rgba(0,0,0,0.38)" : "#F57C00",
                        "&:hover": { backgroundColor: jobDetailsLoading ? "transparent" : "#FFF3E0" },
                    }}
                >
                    <EditOutlinedIcon sx={{ fontSize: 20 }} />
                    {jobDetailsLoading ? "Loading..." : "Edit"}
                </MenuItemDropdown>
                <MenuItemDropdown onClick={handleDelete} disabled={deleteStatus.loading} sx={{ gap: 1, py: 1.5, color: "#D32F2F", "&:hover": { backgroundColor: "#FFEBEE" }, }}>
                    <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                    Delete
                </MenuItemDropdown>
            </Menu>

            {/* Preview Dialog */}
            <PreviewDialogBox
                open={previewOpen}
                onClose={handlePreviewClose}
                jobId={selectedJobData?.id || currentJobId}
                formData={selectedJobData}
                selectedBenefits={selectedBenefits}
                benefits={benefits}
                handleBenefitToggle={handleBenefitToggle}
                selectedSkills={selectedSkills}
                skills={skills}
                handleSkillToggle={handleSkillToggle}
                onSkillsChange={handleSkillsChange}
                handleSubmit={handleSubmit}
                isEditMode={isEditMode}
                isSubmitting={updateStatus.loading}
                onFieldChange={handleFieldChange}
                onBenefitsChange={handleBenefitsChange}
                updateError={updateStatus.error}
                updateSuccess={updateStatus.success}
                hasChanges={checkHasChanges()}
            />
        </Box>
    );
};

export default Jobs;
